'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useRouter } from 'next/navigation';
import { supabase, isSupabaseConfigured } from '../lib/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  isConfigured: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkEmailVerification: (userId: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    // Check if Supabase is configured
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    // Get initial session with improved error handling
    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          // Silently handle all session errors
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      } catch (err: any) {
        // Silently handle all initialization errors
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes with error boundary
    try {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        setError(null);
      });

      return () => {
        try {
          subscription.unsubscribe();
        } catch (err) {
          // Silently handle unsubscribe errors
        }
      };
    } catch (err) {
      setLoading(false);
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    if (!isSupabaseConfigured) {
      setError('Authentication service is not configured');
      throw new Error('Authentication service is not configured');
    }

    try {
      setError(null);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setError(error.message);
        throw error;
      }

      setUser(data.user);
      setSession(data.session);
      setLoading(false);
    } catch (err: any) {
      const errorMessage = err?.message?.includes('fetch') 
        ? 'Cannot connect to authentication service. Please check your internet connection.' :'Failed to sign in. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const signUp = async (email: string, password: string) => {
    if (!isSupabaseConfigured) {
      setError('Authentication service is not configured');
      throw new Error('Authentication service is not configured');
    }

    try {
      setError(null);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        setError(error.message);
        throw error;
      }

      setUser(data.user);
      setSession(data.session);
      setLoading(false);
    } catch (err: any) {
      const errorMessage = err?.message?.includes('fetch')
        ? 'Cannot connect to authentication service. Please check your internet connection.' :'Failed to sign up. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const signOut = async () => {
    if (!isSupabaseConfigured) {
      setUser(null);
      setSession(null);
      return;
    }

    try {
      setError(null);
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
    } catch (err: any) {
      // Always clear auth state on sign out, even if request fails
      setUser(null);
      setSession(null);
    }
  };

  const checkEmailVerification = async (userId: string): Promise<boolean> => {
    if (!isSupabaseConfigured) {
      return false;
    }

    try {
      const { data: member, error } = await supabase
        .from('members')
        .select('email_verified')
        .eq('user_id', userId)
        .single();

      if (error) {
        return false;
      }

      return member?.email_verified ?? false;
    } catch (err) {
      return false;
    }
  };

  const value = {
    user,
    session,
    loading,
    error,
    isConfigured: isSupabaseConfigured,
    signIn,
    signUp,
    signOut,
    checkEmailVerification
  };

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:191:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="191" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}